import React, { memo, useCallback, useContext, useState } from 'react'
import {
  Button,
  IconButton,
  DialogActions,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  InputLabel
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { TenantsContext } from '../../../context/tenants.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import AddFoto from '../../AbmAnuncios/widgets/addFoto'
import { uploadFotoEntrenamiento } from '../../AdminProducts/services/rubros.service'
import { Rubro } from '../AbmRubros/types/rubro'

interface NuevaFotoProps {
  open: boolean
  setOpen: (op: boolean) => void
  rubros: Rubro[]
  isMobile: boolean
  loading: boolean
  setLoading: (l: boolean) => void
  updateData: () => Promise<void>
}

const MAX_TITULO_LENGTH = 85

const NuevaFoto: React.FC<NuevaFotoProps> = ({ open, setOpen, rubros, isMobile, loading, setLoading, updateData }) => {
  const [dropError, setDropError] = useState<string>(null)
  const [rubroError, setRubroError] = useState<string>(null)
  const [foto, setFoto] = useState<any>(null)
  const [filename, setFilename] = useState<string>(null)
  const [codRubros, setCodRubros] = useState<string[]>([])
  const { tenant } = useContext(TenantsContext)

  const handleClose = () => {
    setFoto(null)
    setCodRubros([])
    setDropError(null)
    setRubroError(null)
    setFilename(null)
    setOpen(false)
  }

  const onSubmit = useCallback(async () => {
    if (!foto) return SnackBarUtils.error('Debes agregar una foto')
    if (codRubros?.length === 0) return setRubroError('Debes agregar por lo menos un rubro')
    const imagenBase64 = foto.includes(',') ? foto.split(',')[1] : foto
    try {
      setLoading(true)
      await uploadFotoEntrenamiento(tenant, filename, imagenBase64, codRubros)
      SnackBarUtils.success('Imagen publicada con éxito')
      updateData()
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(e.message === 'Network Error' ? 'Error al publicar imagen' : e.message)
    } finally {
      setLoading(false)
      handleClose()
    }
  }, [tenant, filename, codRubros, foto])

  if (!rubros) return
  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            overflowX: 'hidden'
          }
        }}
      >
        <DialogTitle>
          Agregar Nueva Foto de Entrenamiento{' '}
          <IconButton
            disabled={loading}
            edge="end"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <Stack sx={{ width: '100%' }} spacing={1}>
            <InputLabel
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%'
              }}
            >
              Archivo de Imagen
            </InputLabel>
            <AddFoto foto={foto} setFoto={setFoto} setDropError={setDropError} setFilename={setFilename} />
          </Stack>

          <Stack sx={{ width: '100%', display: 'flex', marginTop: '1rem' }}>
            <Autocomplete
              disabled={loading}
              multiple
              options={rubros}
              getOptionLabel={(option) => option.nombreAnalytics}
              value={rubros.filter((r) => codRubros?.includes(r.codRubro))}
              onChange={(_, value) => {
                setCodRubros(value.map((r) => r.codRubro))
              }}
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField {...params} label="Seleccioná rubros" error={!!rubroError} helperText={rubroError || ''} />
              )}
            />
          </Stack>

          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button disabled={loading} variant="contained" color="error" onClick={handleClose} fullWidth>
              Cancelar
            </Button>
            <Button onClick={onSubmit} disabled={loading} variant="contained" color="success" type="submit" fullWidth>
              Guardar
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
      {dropError && (
        <Dialog
          open={dropError !== null || dropError !== undefined}
          onClose={() => setDropError(undefined)}
          maxWidth="lg"
        >
          <DialogTitle>Errores en la subida de imagenes</DialogTitle>
          <DialogContent>{dropError}</DialogContent>
          <DialogActions>
            <Button onClick={() => setDropError(undefined)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default memo(NuevaFoto)
